import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { cn } from '@/lib/cn';
import { useTheme } from '@/state/ThemeContext';

/**
 * Piezas de movimiento que envuelven al contenido.
 *
 * Son dos y hacen cosas distintas:
 *
 *   - `PageTransition` corta entre pantallas. Cada navegación entra con un
 *     fundido y un empuje mínimo, como un corte de montaje.
 *   - `Reveal` deja que las secciones aparezcan al llegar a ellas en vez de
 *     estar ya puestas. Es lo que convierte un scroll en un travelling.
 *
 * Las dos se apagan con `prefers-reduced-motion`, y ninguna retiene el
 * contenido: si la animación no llega a correr, lo que hay debajo se ve igual.
 * Eso importa más de lo que parece —una animación de entrada mal hecha es la
 * forma más fácil de dejar una página en blanco para siempre—.
 */

// ---------------------------------------------------------------------------
// Transición entre pantallas
// ---------------------------------------------------------------------------

export function PageTransition({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  const { reducedMotion } = useTheme();

  if (reducedMotion) return <>{children}</>;

  return (
    // La `key` es la ruta: al cambiar, React desmonta y vuelve a montar, y la
    // animación arranca de cero. Sin ella el nodo se reutiliza y no pasa nada.
    <div key={pathname} className="animate-page-in">
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Revelado al entrar en pantalla
// ---------------------------------------------------------------------------

interface RevealProps {
  children: React.ReactNode;
  className?: string;
  /** Retraso en ms, para escalonar varios hermanos. */
  delay?: number;
  /** De dónde entra. Por defecto, desde abajo. */
  from?: 'up' | 'left' | 'right' | 'scale';
  as?: 'div' | 'section' | 'li' | 'article';
}

const FROM: Record<NonNullable<RevealProps['from']>, string> = {
  up: 'animate-reveal-up',
  left: 'animate-reveal-left',
  right: 'animate-reveal-right',
  scale: 'animate-reveal-scale',
};

export function Reveal({
  children,
  className,
  delay = 0,
  from = 'up',
  as: Tag = 'div',
}: RevealProps) {
  const { reducedMotion } = useTheme();
  const ref = useRef<HTMLElement>(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    if (reducedMotion) {
      setShown(true);
      return;
    }
    const node = ref.current;
    // Sin nodo o sin soporte del observador, se enseña sin más: nunca puede
    // quedarse oculto por un fallo de la animación.
    if (!node || typeof IntersectionObserver === 'undefined') {
      setShown(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setShown(true);
          observer.disconnect();
        }
      },
      // Se dispara un poco antes de que asome, para que la animación esté ya
      // empezada cuando el elemento entra de verdad.
      { rootMargin: '0px 0px -12% 0px', threshold: 0.05 },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [reducedMotion]);

  // La etiqueta es variable (div, section, li…) y TypeScript no sabe casar un
  // `ref` con la union de todas. `ElementType` relaja esa comprobacion sin
  // recurrir a `any`.
  const Component = Tag as React.ElementType;

  return (
    <Component
      ref={ref}
      className={cn(!shown && 'opacity-0', shown && FROM[from], className)}
      style={shown && delay ? { animationDelay: `${delay}ms` } : undefined}
    >
      {children}
    </Component>
  );
}
